import * as React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MfaServices } from "@/services/bedrock/MfaServices";
import { MfaDeliveryMethod } from "@/services/bedrock/dtos";
import { Loader2, Smartphone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useSocialCoachSession } from "@/utils";
import { Svg } from "@/assets";
import { useMfaChallengeContext } from "../context";

export const EnterCode: React.FC = () => {
  const { method, setStep, isLoading, setIsLoading, hasPasskey, code, setCode, onSuccess, error, setError } =
    useMfaChallengeContext();

  const { session, refreshSession } = useSocialCoachSession();

  const handleVerifyCode = async () => {
    if (!code || !method || method === "passkey") return;
    setError(null);
    setIsLoading(true);
    try {
      const result = await MfaServices.verifyChallenge(code, method as MfaDeliveryMethod);
      if (result.status === "success") {
        await refreshSession(true);
        // Phase 2 Refinement: Check if we should prompt for better MFA
        const needsBetterMfa = !session?.mfaPhone?.verifiedAt && !hasPasskey;
        if (method === "email" && needsBetterMfa) {
          setStep("PROMPT_SETUP");
        } else {
          toast.success("Identity verified");
          onSuccess();
        }
      } else {
        setError("Incorrect verification code. Please try again.");
      }
    } catch (e: any) {
      const apiError =
        e.response?.status === 401
          ? "Incorrect verification code. Please try again."
          : e.response?.data?.error || e.response?.data?.message || e.message || "Verification failed";
      setError(apiError);
      toast.error(apiError);
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const isAlphanumeric = method === "slack";
    const regex = isAlphanumeric ? /[^a-zA-Z0-9]/g : /\D/g;
    const pastedData = e.clipboardData.getData("text").replace(regex, "").toUpperCase().slice(0, 6);
    if (pastedData) {
      setCode(pastedData);
      const focusIndex = Math.min(pastedData.length, 5);
      document.getElementById(`code-${focusIndex}`)?.focus();
    }
  };

  return (
    <div className="space-y-6 pt-4 animate-in fade-in duration-200">
      <div className="text-center space-y-2">
        <div className="h-12 w-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
          {method === "sms" ? (
            <Smartphone className="h-6 w-6" />
          ) : method === "slack" ? (
            <img src={Svg.SlackSVG} alt="Slack" className="h-6 w-6" />
          ) : (
            <Mail className="h-6 w-6" />
          )}
        </div>
        <p className="text-sm text-slate-600 font-medium">
          Enter the 6-digit code we sent to your{" "}
          {method === "sms" ? "phone" : method === "slack" ? "Slack account" : "email"}.
        </p>
      </div>

      {error && (
        <div className="p-3.5 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-xs font-semibold flex items-start gap-2.5 animate-in fade-in duration-200">
          <div className="shrink-0 h-4 w-4 bg-rose-200 rounded-full flex items-center justify-center text-[10px] font-extrabold text-rose-700">
            !
          </div>
          <p className="flex-1 leading-normal">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex justify-center gap-2">
          {[...Array(6)].map((_, i) => {
            const isAlphanumeric = method === "slack";
            const regex = isAlphanumeric ? /[^a-zA-Z0-9]/g : /\D/g;
            return (
              <Input
                key={i}
                id={`code-${i}`}
                className="w-12 h-14 text-center text-xl font-bold p-0 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 shadow-sm uppercase animate-in fade-in duration-200"
                value={code[i] || ""}
                onPaste={handlePaste}
                onChange={e => {
                  const val = e.target.value.replace(regex, "").toUpperCase().slice(-1);
                  const newCode = code.split("");
                  newCode[i] = val;
                  const joined = newCode.join("");
                  setCode(joined);
                  if (val && i < 5) {
                    document.getElementById(`code-${i + 1}`)?.focus();
                  }
                }}
                onKeyDown={e => {
                  if (e.key === "Backspace" && !code[i] && i > 0) {
                    document.getElementById(`code-${i - 1}`)?.focus();
                  } else if (e.key === "Enter" && code.length === 6 && !isLoading) {
                    e.preventDefault();
                    handleVerifyCode();
                  }
                }}
                inputMode={isAlphanumeric ? "text" : "numeric"}
                autoComplete="one-time-code"
                autoFocus={i === 0}
              />
            );
          })}
        </div>

        <div className="flex flex-col gap-3">
          <Button
            className="w-full h-12 font-bold shadow-md"
            onClick={handleVerifyCode}
            disabled={isLoading || code.length < 6}
          >
            {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify & Continue"}
          </Button>
          <Button
            variant="ghost"
            className="w-full text-slate-400 hover:text-slate-600 font-semibold h-10"
            onClick={() => {
              setCode("");
              setError(null);
              setStep("SELECT_METHOD");
            }}
            disabled={isLoading}
          >
            Use a different method
          </Button>
        </div>
      </div>
    </div>
  );
};
